import { Close } from "@mui/icons-material";
import {
  Alert,
  Autocomplete,
  Button,
  FormControl,
  IconButton,
  Modal,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useCallback, useState } from "react";
import { useGetAllEmployeesQuery } from "src/api/services/employeesApi";
import { useBulkUpdateWaitingRoomsMutation } from "src/api/services/tasksApi";
import { EmployeesView } from "src/api/types";
import { useSelection } from "src/context/ItemsSelectionProvider/useSelection";
import { useTasks } from "src/context/TasksProvider";
import { isError } from "src/utils/isError";
import { desktopSx } from "src/utils/styles/modalStyles";

interface Props {
  open: boolean;
  handleClose: () => void;
}

const ForwardTaskModal = ({ open, handleClose }: Props) => {
  const { selected, setSelected } = useSelection();
  const { getNextTask } = useTasks();
  const { data: clinicians } = useGetAllEmployeesQuery({
    role: "Provider",
  });
  const [reassignRooms] = useBulkUpdateWaitingRoomsMutation();
  const [selectedClinician, setSelectedClinician] =
    useState<EmployeesView | null>(null);
  const [reason, setReason] = useState<string>("");
  const [error, setError] = useState<string | null>("");
  const [success, setSuccess] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);

  const handleReassignRooms = useCallback(async () => {
    if (selectedClinician == null) {
      setError("Please make sure that a clinician has been selected");
      return;
    }

    if (reason.length === 0) {
      setError("Please provide your reasoning for forwarding this task");
      return;
    }
    setLoading(true);

    try {
      await reassignRooms({
        assignee_id: selectedClinician.id,
        assignee_type: "Provider",
        ids: selected,
        status: "ASSIGNED",
        assigned_at: new Date().toISOString(),
        forwarding_reason: reason,
        queue: "Provider",
        isForwarded: true,
      }).unwrap();

      setSuccess(`Successfully forwarded room: ${selected.join(", ")}`);
      getNextTask();

      setSelectedClinician(null);
      setReason("");
      setSelected([]);
    } catch (error) {
      if (isError(error)) {
        setError(error.data.message);
      } else {
        setError("Room forwarding was unsuccessful, please try again");
      }
    } finally {
      setLoading(false);
    }
  }, [
    getNextTask,
    reason,
    reassignRooms,
    selected,
    selectedClinician,
    setSelected,
  ]);

  const onCloseModal = useCallback(() => {
    handleClose();
    setSuccess("");
    setError("");
  }, [handleClose]);

  return (
    <Modal open={open}>
      <Stack sx={desktopSx}>
        <Stack gap={2}>
          <Stack
            direction={"row"}
            justifyContent="space-between"
            alignItems="center"
            gap={10}
          >
            <Typography variant="h5" fontWeight={600}>
              Waiting Room Forwarding Service
            </Typography>
            <IconButton onClick={onCloseModal}>
              <Close fontSize="medium" />
            </IconButton>
          </Stack>
          <Alert severity="warning">
            <Typography
              color="warning"
              sx={{ textDecoration: "underline" }}
              variant="h6"
            >
              Rooms selected
            </Typography>
            <Typography fontWeight={600} variant="h6">
              {selected.join(", ")}
            </Typography>
          </Alert>
          <FormControl required>
            <Autocomplete
              options={clinicians?.data ?? []}
              getOptionLabel={(c) =>
                c
                  ? `${c.first_name} ${c.last_name} - ${c.roles?.map((r) => r.display_name ?? r.name).join(", ")}`
                  : ""
              }
              value={selectedClinician}
              onChange={(_, newValue) => setSelectedClinician(newValue)}
              renderInput={(params) => (
                <TextField {...params} label="Clinician" required />
              )}
              isOptionEqualToValue={(o, v) => o.id === v.id}
            />
          </FormControl>
          <FormControl required>
            <TextField
              required
              label="Forwarding reason"
              onChange={(e) => setReason(e.target.value)}
            />
          </FormControl>
          {error ? (
            <Alert severity="error" variant="filled">
              {error}
            </Alert>
          ) : success ? (
            <Alert severity="success" variant="filled">
              {success}
            </Alert>
          ) : null}
          <Button
            loading={loading}
            disabled={!selectedClinician}
            onClick={handleReassignRooms}
          >
            Forward room
          </Button>
        </Stack>
      </Stack>
    </Modal>
  );
};

export default ForwardTaskModal;
